/**
 * The `_optimistic` payload the cart store needs to draw a row before the add
 * request comes back (see `store/cart.js`). Built in one place so every add
 * site carries the same fields — a missing one shows as a gap in the row until
 * the server answers, and each site getting it slightly wrong is how that
 * happens.
 *
 * Two traps this exists to avoid:
 *
 *   - Prices must be in cents. Anything that has been through Liquid's `money`
 *     filter is a display string and cannot be used here.
 *   - `nil | img_url` hands back the admin's "no image" placeholder, a
 *     non-empty string that beats any `||` fallback. A variant image is only
 *     used when the variant actually has one.
 */

const hasImage = value =>
  typeof value === "string" && value !== "" && !/no-image/.test(value);

const imageOf = (product, variant) => {
  const candidates = [
    // `json-variant.liquid` emits the variant's picture as `variantImage` or
    // `productImage`, not `featured_image` — looking only for the latter meant
    // always falling through to the product's picture, so the cart row opened
    // with a different image than the card the shopper had just clicked.
    variant?.variantImage?.src,
    variant?.productImage?.src,
    variant?.featured_image?.src,
    variant?.featured_image,
    product?.featured_image?.src,
    product?.featured_image,
    product?.images?.[0]?.src,
    product?.images?.[0],
  ];
  return candidates.find(hasImage) || "";
};

/**
 * The theme's own price record for a variant on a given plan. `json-product`
 * emits one per variant and per selling plan, with the plan's price and the
 * one-time price as its compare-at — worked out server-side from the variant's
 * selling plan allocations, which the variant JSON itself does not carry. It is
 * what the product form reads to show a price, so it is what the cart row
 * should open with.
 */
const priceRecord = (product, variantId, planId) => {
  const prices = product?.prices;
  if (!Array.isArray(prices)) return null;

  const forVariant = prices.filter(p => Number(p.id) === Number(variantId));
  const wanted = String(planId ?? "");

  return (
    forVariant.find(p => String(p.selling_plan ?? "") === wanted) ||
    (wanted ? null : forVariant.find(p => !p.selling_plan)) ||
    null
  );
};

/**
 * @param product Shopify product JSON (prices in cents)
 * @param variant the variant being added
 * @param planId selling plan id, when subscribing — its price is what the row
 *   should show, not the one-time price
 */
export const optimisticFromProduct = (product, variant, planId = null) => {
  const allocation = planId
    ? variant?.selling_plan_allocations?.find(
        a => a.selling_plan_id == planId,
      )
    : null;
  const record = priceRecord(product, variant?.id, planId);

  return {
    product_title: product?.title,
    variant_title: variant?.title,
    // The row finds a product's subscription plans in
    // `window.Shopify.products_subscribe`, matched on `product_id` — data
    // already on the page, so the "Upgrade to subscription" button can appear
    // at once. Without the id it had to fall back to fetching the product JSON,
    // and the button turned up a request later.
    product_id: product?.id,
    // The record first: on a subscription it is the only place the plan's price
    // and its strike-through are already worked out, so without it the row
    // opened at the one-time price with no strike until the server answered.
    price: record?.price ?? allocation?.price ?? variant?.price,
    // Both spellings: `json-variant.liquid` emits `compareAtPrice`, the upsell
    // and cart serializers emit `compare_at_price`.
    compare_at_price:
      record?.compareAtPrice ??
      allocation?.compare_at_price ??
      variant?.compare_at_price ??
      variant?.compareAtPrice,
    image: imageOf(product, variant),
    handle: product?.handle,
  };
};
